import { ForgotPasswordModal as SharedModal } from "@ever-tutor/et-ui";
import {
  useNotificationStore,
  NotificationType,
} from "@/store/useNotificationStore";
import { emailOtp, validateResetEmail } from "@/lib/auth-client";

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  debugMode?: boolean;
}

const ForgotPasswordModal = ({
  isOpen,
  onClose,
  debugMode = false,
}: ForgotPasswordModalProps) => {
  const { showNotification } = useNotificationStore();

  const handleSendCode = async (email: string) => {
    try {
      const validation = await validateResetEmail(email);
      if (!validation.valid) {
        if (validation.error === "GOOGLE_ACCOUNT") {
          return { success: false, isGoogleAccount: true };
        }
        return { success: false, error: validation.error };
      }

      const { error } = await emailOtp.sendVerificationOtp({
        email,
        type: "forget-password",
      });

      if (error) {
        showNotification(
          error.message || "Failed to send code",
          NotificationType.WARNING,
        );
        return { success: false, error: error.message };
      }

      showNotification(
        "Reset code sent to your email",
        NotificationType.SUCCESS,
      );
      return { success: true };
    } catch (err) {
      return { success: false, error: "An unexpected error occurred" };
    }
  };

  const handleVerifyCode = async (email: string, code: string) => {
    try {
      const { error } = await emailOtp.checkVerificationOtp({
        email,
        type: "forget-password",
        otp: code,
      });

      if (error) {
        return {
          success: false,
          error:
            error.code === "OTP_EXPIRED"
              ? ("expired" as const)
              : ("invalid" as const),
        };
      }
      return { success: true };
    } catch (err) {
      return { success: false, error: "other" as const };
    }
  };

  const handleUpdatePassword = async (
    email: string,
    otp: string,
    password: string,
  ) => {
    try {
      const { error } = await emailOtp.resetPassword({
        email,
        otp,
        password,
      });

      if (error) {
        return { success: false, error: error.message };
      }

      showNotification(
        "Password updated successfully!",
        NotificationType.SUCCESS,
      );
      return { success: true };
    } catch (err) {
      return { success: false, error: "An unexpected error occurred" };
    }
  };

  const handleResendCode = async (email: string) => {
    try {
      const validation = await validateResetEmail(email);
      if (!validation.valid) {
        if (validation.error === "GOOGLE_ACCOUNT") {
          return { success: false, isGoogleAccount: true };
        }
        return { success: false, error: validation.error };
      }

      const { error } = await emailOtp.sendVerificationOtp({
        email,
        type: "forget-password",
      });

      if (error) {
        showNotification(
          error.message || "Failed to resend code",
          NotificationType.WARNING,
        );
        return { success: false, error: error.message };
      }

      showNotification("Reset code resent", NotificationType.SUCCESS);
      return { success: true };
    } catch (err) {
      return { success: false, error: "An unexpected error occurred" };
    }
  };

  return (
    <SharedModal
      isOpen={isOpen}
      onClose={onClose}
      onSendCode={handleSendCode}
      onVerifyCode={handleVerifyCode}
      onUpdatePassword={handleUpdatePassword}
      onResendCode={handleResendCode}
      debugMode={debugMode}
      aria-label="Forgot password dialog"
    />
  );
};

export default ForgotPasswordModal;
